import React, { useEffect, useState } from "react";
import {
  Button,
  TextField,
  Paper,
  InputAdornment,
  Modal,
  Tooltip,
} from "@mui/material";
import useSingleModuleProgress from "src/swr/hooks/RetailReports/useSingleModuleProgress";
import { ArrowRightCircle, Loader, MapPin, Search, User } from "react-feather";
import styled from "styled-components";
import useAudienceToolStore from "src/store/useAudienceToolStore";
import { TABLE_STATES } from "src/components/audience/AudienceTool/helpers";

const ModalWrapper = styled(Paper)`
  width: 480px;
  max-height: 600px;
  min-height: 300px;
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  border-radius: 12px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  border: 2px solid black;
`;

const StyledButton = styled(Button)`
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px 8px;
  background: aliceblue;
  border: 1px dashed gainsboro;
  gap: 4px;
  margin-left: auto;

  &:hover {
    background: aliceblue;
    border: 1px dashed #337ab7;
  }
`;

const ViewUsersInAudience = () => {
  const moduleId = useAudienceToolStore((state) => state.moduleId);
  const tableIsSaving = useAudienceToolStore(
    (state) => state.tableState === TABLE_STATES.SAVING,
  );

  const [open, setOpen] = useState(false);
  const { data, isLoading, isValidating, isError } =
    useSingleModuleProgress(moduleId);

  if (isError) {
    return null;
  }

  if (isLoading) {
    return <div style={{ height: 32, minHeight: 32 }} />;
  }

  const isDisabled = tableIsSaving || isValidating;

  return (
    <>
      <div style={{ marginLeft: "auto" }}>
        <Tooltip title="View Audience" arrow>
          <StyledButton
            disabled={isDisabled}
            variant="text"
            onClick={() => setOpen((p) => !p)}
          >
            <span
              style={{
                color: isDisabled ? "lightgray" : "black",
              }}
            >
              Audience Size:
            </span>
            <span
              style={{
                fontWeight: 600,
                color: isDisabled ? "lightgray" : "#337ab7",
              }}
            >
              {isDisabled ? "--" : data?.assigned_users?.length}
              {isDisabled ? (
                <Loader
                  height={12}
                  style={{
                    transform: "translate(-2px, 2px) ",
                  }}
                />
              ) : (
                <ArrowRightCircle
                  height={12}
                  style={{
                    transform: "translate(-2px, 2px) rotate(-40deg)",
                    color: isDisabled ? "lightgray" : "#337ab7",
                  }}
                />
              )}
            </span>
          </StyledButton>
        </Tooltip>
      </div>
      <Modal open={open} onClose={() => setOpen(false)}>
        <div>
          <Inner assignedUsers={data?.assigned_users} />
        </div>
      </Modal>
    </>
  );
};

export default ViewUsersInAudience;

const getLocationStr = (locationArray) => {
  if (!locationArray || locationArray?.length === 0) return "---/---";
  if (locationArray?.length === 1) return locationArray?.[0]?.name;

  return locationArray?.map((l) => l?.name)?.join(", ");
};

const Inner = ({ assignedUsers }) => {
  const [search, setSearch] = useState("");

  const [searchResults, setSearchResults] = useState([]);

  useEffect(() => {
    if (search.length < 3) return;

    setSearchResults(
      assignedUsers.filter((user) =>
        `${user?.first_name} ${user?.last_name}`
          .toLowerCase()
          .includes(search.toLowerCase()),
      ),
    );
  }, [search]);

  if (!assignedUsers || assignedUsers?.length === 0) return null;

  const isSearching = search?.length >= 3;

  return (
    <ModalWrapper>
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "flex-start",
          flexDirection: "column",
        }}
      >
        <span style={{ fontSize: 16, fontWeight: 600 }}>Audience</span>
        <span>{assignedUsers?.length} team members total</span>
      </div>
      <div style={{ minHeight: 8 }} />
      <TextField
        size="small"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ minWidth: 300 }}
        color="primary"
        autoFocus
        fullWidth
        variant="outlined"
        placeholder="Search..."
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search height={13} width={13} />
            </InputAdornment>
          ),
        }}
      />
      <div style={{ minHeight: 8 }} />
      <div
        style={{
          width: "100%",
          display: "flex",
          flexWrap: "wrap",
          alignItems: "flex-start",
          justifyContent: "flex-start",
          alignContent: "flex-start",
          gap: 0,

          flex: 1,
          maxHeight: 360,
          minHeight: 300,
          borderRadius: 8,

          overflow: "auto",
          paddingRight: 8,
        }}
      >
        {isSearching && searchResults?.length === 0 && (
          <span style={{ padding: 8, color: "darkgrey" }}>
            no results for search...
          </span>
        )}
        {(isSearching ? searchResults : assignedUsers)?.map((user, i) => (
          <div
            key={`${user?.first_name}-${user?.last_name}-${i}`}
            style={{
              padding: "8px",

              borderBottom: "1px solid gainsboro",
              background: "white",
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              gap: 4,
            }}
          >
            <User
              height={18}
              width={18}
              color="slategrey"
              style={{ padding: 3, borderRadius: 4, background: "#eeeeee" }}
            />
            <span
              style={{
                fontSize: 12,
                maxWidth: 300,
                textOverflow: "ellipsis",
                overflow: "hidden",
                whiteSpace: "nowrap",
                fontWeight: 500,
                flex: 1,
              }}
            >
              {user?.first_name} {user?.last_name}
            </span>
            <span
              style={{
                marginLeft: "auto",
                color: "darkgrey",
                fontSize: 11,
                maxWidth: 160,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              <MapPin
                height={11}
                width={11}
                style={{ transform: "translate(1px, 1px)" }}
              />{" "}
              {getLocationStr(user?.locations)}
            </span>
          </div>
        ))}
      </div>
    </ModalWrapper>
  );
};
